import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Search, Settings, HelpCircle, Bookmark, Home as HomeIcon, BarChart3, Grid3x3,
  Calculator, Receipt, ShoppingCart, Sliders, Building2, ChevronDown, Loader2, X, Landmark,
  Pencil, Trash2,
} from 'lucide-react';
import Lists from './Lists';
import DesktopImport from './DesktopImport';
import ImportWizard from './ImportWizard';
import Banking from './Banking';
import Vendors from './Vendors';
import YearEnd from './YearEnd';
import ReportsCenter from './Reports';
import BusinessSnapshot from './ui/snapshot';
import { ImportData, ExportData, BankReview, OnlineImport, AccountingSettings } from './ui/tools';
import Home from './Home';
import FinanceForm from './ui/forms';
import OnlineList from './ui/lists';
import { ChartOfAccounts, Register } from './ui/accounting';
import { RulesPage, ReconcilePage, RecurringPage, ReceiptsPage, IntegrationPage, MyAccountantPage } from './ui/accounting2';
import { SalesOverview, ExpensesOverview, CustomerHubOverview } from './ui/overviews';
import { ExpenseTransactionsPage, VendorsPage, BillsPage, BillPaymentsPage, SalesTransactionsPage, InvoicesPage, ProductsPage } from './ui/accounting3';
import { AccountAndSettings, MileagePage, ContractorsPage, BankBalanceCard } from './ui/settings13';
import { CustomersLeadsPage } from './ui/accounting3';
import { BusinessTaxOverview, TaxSummaryPage } from './ui/businessTax';
import { ReclassifyPage, AdvancedSearchPage, Nine99Page, FormStylesPage, CustomFieldsPage, AttachmentsPage } from './ui/batch3';
import { setDateFormat } from './money';
import { TasksPage } from './ui/tasks';
import { FeaturesContext } from './featureContext';
import { FinanceChargesPage } from './ui/charges';
import { FixedAssetsPage } from './ui/fixedAssets';
import { EstimatesPage } from './ui/estimates';
import { PurchaseOrdersPage } from './ui/purchaseOrders';
import { NonprofitSetupPage } from './ui/nonprofitSetup';
import { SalesTaxPage } from './ui/salesTax';
import { InventoryPage } from './ui/inventory';
import { PayrollPage } from './ui/payroll';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const OLFIN = `${API_URL}/api/olfin`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const STARTER = [['none', 'None (blank chart)'], ['nonprofit', 'Non-Profit (UCOA)']];
const EMPTY = { name: '', legal_name: '', fiscal_year_start_month: '1', tax_form: '', starter_chart: 'none' };

// Create (+) menu — 4 columns incl Team (Add contractor) and Other ▸ Task.
const CREATE = [
  ['Customers', [['Invoice', 'form', 'invoice', 'invoices'], ['Estimate', 'form', 'estimate', 'estimates'], ['Receive payment', 'form', 'payment', 'invoices'], ['Statement', 'form', 'statement', 'invoices'], ['Statement charge', 'form', 'stmtcharge', 'invoices'], ['Assess finance charges', 'finance_charges', null, 'invoices'],
    ['Credit memo', 'form', 'creditmemo', 'invoices'], ['Sales receipt', 'form', 'salesreceipt', 'invoices'], ['Refund receipt', 'form', 'refund', 'invoices'], ['Add customer', 'form', 'customer']]],
  ['Vendors', [['Expense', 'form', 'expense'], ['Check', 'form', 'check'], ['Bill', 'form', 'bill', 'bills'], ['Purchase order', 'form', 'po', 'purchase_orders'], ['Pay bills', 'form', 'paybills', 'bills'],
    ['Vendor credit', 'form', 'vendorcredit', 'bills'], ['Credit card credit', 'form', 'cccredit', 'credit_cards'], ['Print checks', 'banking', 'print'], ['Add vendor', 'form', 'vendor']]],
  ['Team', [['Add contractor', 'contractors', null], ['Run payroll', 'payroll', 'run', 'payroll'], ['Pay payroll liabilities', 'payroll', 'liabilities', 'payroll'], ['Add employee', 'payroll', 'employees', 'payroll']]],
  ['Other', [['Bank deposit', 'form', 'deposit'], ['Transfer', 'form', 'transfer'], ['Journal entry', 'form', 'journal'], ['Pay sales tax', 'sales_tax', 'pay', 'sales_tax'], ['Pay down credit card', 'form', 'paydown', 'credit_cards'], ['Add product/service', 'form', 'item', 'products'], ['Task', 'tasks', null]]],
];
// Gear (⚙) menu — 4-column panel grouped by legacy order. Untagged entries are the core every organization sees;
// a 4th element names the optional feature (olfin/features.py) that must be on for the entry to show.
const GEAR = [
  ['Your Company', [['Account and settings', 'company_settings', null], ['Features (Simple mode)', 'company_settings', 'features'], ['Manage users', 'accountant', null], ['Custom form styles', 'form_styles', null, 'invoices'], ['Nonprofit setup', 'np_setup', null], ['Default report settings', 'company_settings', 'reports'], ['Chart of accounts', 'coa', null]]],
  ['Lists', [['All lists', 'lists', null, 'setup_tools'], ['Products and services', 'products_page', null, 'products'], ['Fixed assets', 'fixed_assets', null, 'fixed_assets'], ['Recurring transactions', 'recurring', null, 'recurring'], ['Attachments', 'attachments', null, 'receipts'], ['Custom fields', 'custom_fields', null, 'setup_tools'], ['Rules', 'rules', null]]],
  ['Tools', [['Reclassify transactions', 'reclassify', null, 'setup_tools'], ['Import data', 'importdata', null], ['Import desktop data', 'import', null, 'setup_tools'], ['Export data', 'exportdata', null, 'setup_tools'], ['Reconcile', 'reconcile', null], ['Budgeting', 'reports', null], ['Audit log', 'audit', null, 'setup_tools'], ['Import from online accounting', 'onlineimport', null, 'setup_tools'], ['Import wizard', 'wizard', null, 'setup_tools'], ['Year-end package', 'yearend', null, 'year_end']]],
  ['Profile', [['Feedback', 'feedback', null]]],
];
// All apps — Accounting menu = the 8 pages in the exact order AJ specified.
const ALLAPPS = [
  ['Accounting', [['Bank transactions', 'bankreview', null], ['Integration transactions', 'integration', null, 'ol_link'],
    ['Receipts', 'receipts', null, 'receipts'], ['Reconcile', 'reconcile', null], ['Rules', 'rules', null],
    ['Chart of accounts', 'coa', null], ['Recurring transactions', 'recurring', null, 'recurring'], ['My accountant', 'accountant', null]]],
  ['Expenses & Bills', [['Overview', 'expenses_overview', null], ['Expense transactions', 'expense_txns', null], ['Vendors', 'vendors_page', null], ['Bills', 'bills_page', null, 'bills'], ['Purchase orders', 'po_page', null, 'purchase_orders'], ['Bill payments', 'bill_payments', null, 'bills'], ['Mileage', 'mileage', null, 'mileage'], ['Contractors', 'contractors', null], ['1099s', 'v1099', null]]],
  ['Sales & Get Paid', [['Overview', 'sales_overview', null, 'invoices'], ['Sales transactions', 'sales_txns', null, 'invoices'], ['Invoices', 'invoices_page', null, 'invoices'], ['Estimates', 'estimates_page', null, 'estimates'], ['Products & services', 'products_page', null, 'products'], ['Inventory', 'inventory', null, 'inventory']]],
  ['Payroll', [['Pay employees', 'payroll', 'run', 'payroll'], ['Paychecks', 'payroll', 'paychecks', 'payroll'], ['Pay liabilities', 'payroll', 'liabilities', 'payroll'], ['Employees', 'payroll', 'employees', 'payroll'], ['Payroll items', 'payroll', 'items', 'payroll'], ['Payroll settings', 'payroll', 'settings', 'payroll'], ['Tax forms & W-2s', 'payroll', 'forms', 'payroll']]],
  ['Customer Hub', [['Overview', 'customerhub_overview', null, 'invoices'], ['Customers & leads', 'customers_leads', null]]],
  ['Business Tax', [['Overview', 'biztax_overview', null, 'year_end'], ['Sales tax', 'sales_tax', null, 'sales_tax'], ['Tax summary', 'tax_summary', null, 'year_end'], ['Year-end filing', 'yearend', null, 'year_end']]],
];
const PINNABLE = { accounting: ['Accounting', Calculator, 'coa', null], expenses: ['Expenses', Receipt, 'vendors', 'center'], sales: ['Sales', ShoppingCart, 'customers', 'center'] };
const PIN_SECTION = { accounting: 'Accounting', expenses: 'Expenses & Bills', sales: 'Sales & Get Paid' };
const findSection = (view, tab) => ALLAPPS.find(([, items]) => items.some(([, v, t]) => v === view && (t || null) === (tab || null)));
const tid = (s) => String(s).toLowerCase().replace(/[^a-z]+/g, '-');

export default function FinanceModule({ orgId, isDark = true, firstName = 'Treasurer' }) {
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('home');
  const [panelTab, setPanelTab] = useState(null);
  const [navSeq, setNavSeq] = useState(0);
  const [formCtx, setFormCtx] = useState(null);
  const [menu, setMenu] = useState(''); // '', 'create', 'allapps', 'company', 'settings', 'bookmarks', 'customize'
  const [appsOpen, setAppsOpen] = useState(true); // persistent All-apps sidebar
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [showCreateCo, setShowCreateCo] = useState(false);
  const [coForm, setCoForm] = useState(EMPTY); const [saving, setSaving] = useState(false);
  const [pins, setPins] = useState(() => { try { return JSON.parse(localStorage.getItem('olfin_pins') || '["accounting","expenses","sales"]'); } catch { return ['accounting', 'expenses', 'sales']; } });
  const [bookmarks, setBookmarks] = useState(() => { try { return JSON.parse(localStorage.getItem('olfin_bookmarks') || '[]'); } catch { return []; } });
  const [bmEdit, setBmEdit] = useState(false);
  const [sq, setSq] = useState(''); const [sres, setSres] = useState(null);
  const [regAcct, setRegAcct] = useState(null);
  const [taxForms, setTaxForms] = useState([]);
  useEffect(() => {
    axios.get(`${OLFIN}/tax-lines`).then((r) => {
      const forms = [...new Set((r.data || []).map((x) => x.form).filter(Boolean))];
      setTaxForms(forms.length ? forms : ['990', '1120', '1065']);
    }).catch(() => setTaxForms(['990', '1120', '1065']));
  }, []);
  const panel = isDark ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900';

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const list = (await axios.get(`${OLFIN}/companies`)).data || [];
      setCompanies(Array.isArray(list) ? list : []);
      setSelectedId((p) => p || list[0]?.company_id || '');
    } catch (e) { setError(e?.response?.data?.detail || 'Unable to load finance companies'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => companies.find((c) => c.company_id === selectedId) || null, [companies, selectedId]);
  const base = selected ? `${OLFIN}/companies/${selected.company_id}` : null;
  // Optional features (estimates, POs, sales tax, inventory, payroll): menu entries tagged with a 4th element
  // are shown only when that feature is on for this company (olfin/features.py).
  const [feat, setFeat] = useState({});
  const loadFeatures = useCallback(() => { if (base) axios.get(`${base}/features`).then((r) => setFeat(r.data?.features || {})).catch(() => setFeat({})); }, [base]);
  useEffect(() => { loadFeatures(); }, [loadFeatures]);
  const menuOf = useCallback((m) => m.map(([sec, items]) => [sec, items.filter((it) => !it[3] || feat[it[3]])]).filter(([, items]) => items.length), [feat]);
  const isNonprofit = !!selected && (selected.starter_chart === 'nonprofit' || String(selected.tax_form || '').startsWith('990') || !!selected.nonprofit);

  // G1: load this company's date-display format once; fmtDate() reads it app-wide.
  useEffect(() => {
    if (!base) return;
    axios.get(`${base}/settings`).then((r) => setDateFormat(r.data?.date_format || r.data?.advanced?.date_format || 'MM/DD/YYYY')).catch(() => {});
  }, [base]);

  const go = useCallback((v, tab = null, extra = null) => {
    if (v === 'create') { setMenu('create'); return; }
    if (v === 'allapps') { setAppsOpen(true); setMenu(''); return; }
    setView(v); setPanelTab(tab); setFormCtx(extra); setNavSeq((n) => n + 1); setMenu(''); setSres(null);
  }, []);
  const openTxn = useCallback((arg) => { const txnId = (arg && (arg.txn_id || arg.txnId)) || arg; if (txnId) go('form', 'txndetail', { txnId }); }, [go]);

  const createCo = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const res = await axios.post(`${OLFIN}/companies`, {
        org_id: orgId, name: coForm.name.trim(), legal_name: coForm.legal_name.trim() || null,
        fiscal_year_start_month: parseInt(coForm.fiscal_year_start_month, 10), tax_form: coForm.tax_form || null,
        starter_chart: coForm.starter_chart === 'nonprofit' ? 'nonprofit' : null,
      });
      setShowCreateCo(false); setCoForm(EMPTY); await load();
      if (res.data?.company_id) { setSelectedId(res.data.company_id); go('home'); }
    } catch (e2) { setError(e2?.response?.data?.detail || 'Unable to create company'); }
    finally { setSaving(false); }
  };

  const doSearch = async (term) => {
    const t = (term ?? sq).trim();
    if (!t || !base) { setSres(null); return; }
    try {
      const r = await axios.get(`${base}/search`, { params: { q: t } });
      setSres(r.data);
    } catch { setSres(null); }
  };
  // Debounced live search as the user types.
  useEffect(() => {
    if (!base) return undefined;
    const t = sq.trim();
    if (!t) { setSres(null); return undefined; }
    const h = setTimeout(() => { doSearch(t); }, 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sq, base]);
  const openAdvancedSearch = () => { setFormCtx({ query: sq }); setView('advanced_search'); setPanelTab(null); setNavSeq((n) => n + 1); setMenu(''); setSres(null); };

  const bookmarkCurrent = () => {
    const label = `${view}${panelTab ? ` · ${panelTab}` : ''}`;
    if (bookmarks.some((b) => b.view === view && (b.tab || null) === (panelTab || null))) return;
    const n = [...bookmarks, { view, tab: panelTab, label }];
    setBookmarks(n); localStorage.setItem('olfin_bookmarks', JSON.stringify(n));
  };
  const renameBookmark = (idx, label) => {
    const n = bookmarks.map((b, i) => (i === idx ? { ...b, label } : b));
    setBookmarks(n); localStorage.setItem('olfin_bookmarks', JSON.stringify(n));
  };
  const removeBookmark = (idx) => {
    const n = bookmarks.filter((_, i) => i !== idx);
    setBookmarks(n); localStorage.setItem('olfin_bookmarks', JSON.stringify(n));
  };
  const togglePin = (k) => { const n = pins.includes(k) ? pins.filter((x) => x !== k) : [...pins, k]; setPins(n); localStorage.setItem('olfin_pins', JSON.stringify(n)); };

  const CONTENT = () => {
    if (!base) return <div className="p-10 text-center text-slate-400" data-testid="olfin-empty"><Building2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />{loading ? 'Loading…' : 'No company yet. Add a company to open its books.'}</div>;
    switch (view) {
      case 'home': return <Home base={base} isDark={isDark} firstName={firstName} go={go} orgId={orgId} />;
      case 'reports': return <ReportsCenter base={base} isDark={isDark} company={selected} onOpenTxn={openTxn} go={go} initialReport={formCtx?.report} />;
      case 'business_snapshot': return <BusinessSnapshot base={base} company={selected} go={go} />;
      case 'form': return <FinanceForm tab={panelTab} base={base} isDark={isDark} txnId={formCtx?.txnId} copy={!!formCtx?.copy} initCustomer={formCtx?.customerId} go={go} onClose={() => go('home')} />;
      case 'coa':
        return <ChartOfAccounts base={base} isDark={isDark} go={go}
          onOpenReport={() => { setRegAcct(null); go('reports'); }}
          onOpenRegister={(a) => { setRegAcct(a); setView('register'); }} />;
      case 'register':
        return regAcct ? <Register base={base} isDark={isDark} account={regAcct} onBack={() => go('coa')} onOpenTxn={openTxn} /> : <div className="p-6 text-slate-500">Pick an account from the Chart of accounts.</div>;
      case 'rules': return <RulesPage base={base} />;
      case 'reconcile': return <ReconcilePage base={base} go={go} />;
      case 'recurring': return <RecurringPage base={base} />;
      case 'receipts': return <ReceiptsPage base={base} />;
      case 'integration': return <IntegrationPage olfin={OLFIN} orgId={orgId} base={base} />;
      case 'accountant': return <MyAccountantPage olfin={OLFIN} orgId={orgId} base={base} />;
      case 'sales_overview': return <SalesOverview base={base} go={go} isNonprofit={isNonprofit} />;
      case 'expenses_overview': return <ExpensesOverview base={base} go={go} />;
      case 'customerhub_overview': return <CustomerHubOverview base={base} go={go} />;
      case 'customers_leads': return <CustomersLeadsPage base={base} go={go} isNonprofit={isNonprofit} />;
      case 'biztax_overview': return <BusinessTaxOverview base={base} go={go} isNonprofit={isNonprofit} />;
      case 'tax_summary': return <TaxSummaryPage base={base} isNonprofit={isNonprofit} />;
      case 'expense_txns': return <ExpenseTransactionsPage base={base} go={go} />;
      case 'vendors_page': return <VendorsPage base={base} go={go} />;
      case 'bills_page': return <BillsPage base={base} go={go} />;
      case 'bill_payments': return <BillPaymentsPage base={base} go={go} />;
      case 'sales_txns': return <SalesTransactionsPage base={base} go={go} />;
      case 'invoices_page': return <InvoicesPage base={base} go={go} isNonprofit={isNonprofit} />;
      case 'products_page': return <ProductsPage base={base} go={go} />;
      case 'mileage': return <MileagePage base={base} />;
      case 'contractors': return <ContractorsPage base={base} />;
      case 'company_settings': return <AccountAndSettings base={base} company={selected} initialTab={panelTab} onFeatures={loadFeatures} />;
      case 'reclassify': return <ReclassifyPage base={base} onBack={() => go('home')} />;
      case 'advanced_search': return <AdvancedSearchPage base={base} go={go} onBack={() => go('home')} initialQuery={formCtx?.query || ''} />;
      case 'form_styles': return <FormStylesPage base={base} />;
      case 'custom_fields': return <CustomFieldsPage base={base} />;
      case 'attachments': return <AttachmentsPage base={base} />;
      case 'v1099': return <Nine99Page base={base} />;
      case 'tasks': return <TasksPage base={base} go={go} />;
      case 'finance_charges': return <FinanceChargesPage base={base} go={go} />;
      case 'fixed_assets': return <FixedAssetsPage base={base} />;
      case 'estimates_page': return <EstimatesPage base={base} go={go} />;
      case 'po_page': return <PurchaseOrdersPage base={base} go={go} />;
      case 'np_setup': return <NonprofitSetupPage base={base} go={go} />;
      case 'inventory': return <InventoryPage base={base} go={go} />;
      case 'payroll': return <PayrollPage key={panelTab || 'run'} base={base} go={go} initialTab={panelTab} />;
      case 'sales_tax': return <SalesTaxPage base={base} go={go} initialTab={panelTab} />;
      case 'feedback': return <Home base={base} isDark={isDark} firstName={firstName} go={go} orgId={orgId} />;
      case 'customers': case 'vendors': case 'products': case 'banktxns':
        return <OnlineList view={view} base={base} isDark={isDark} go={go} />;
      case 'banking': return <Banking base={base} isDark={isDark} initialView={panelTab || 'reconcile'} allowed={['register', 'reconcile', 'print']} />;
      case 'lists': return <Lists base={base} isDark={isDark} />;
      case 'import': return <DesktopImport base={base} isDark={isDark} />;
      case 'wizard': return <ImportWizard base={base} isDark={isDark} />;
      case 'yearend': return <YearEnd base={base} isDark={isDark} />;
      case 'importdata': return <ImportData base={base} onBack={() => go('home')} />;
      case 'exportdata': return <ExportData base={base} onBack={() => go('home')} />;
      case 'bankreview': return <BankTransactionsView base={base} onBack={() => go('home')} go={go} />;
      case 'onlineimport': return <OnlineImport base={base} onBack={() => go('home')} />;
      case 'acctsettings': return <AccountingSettings base={base} onBack={() => go('home')} />;
      case 'audit': return <ReportsCenter base={base} isDark={isDark} company={selected} onOpenTxn={openTxn} initialReport="audit_trail" />;
      default: return <Home base={base} isDark={isDark} firstName={firstName} go={go} orgId={orgId} />;
    }
  };

  const railBtn = (Icon, label, onClick, active) => (
    <button onClick={onClick} data-testid={`rail-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      className={`w-full flex flex-col items-center gap-1 py-2.5 rounded-lg text-[11px] ${active ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:bg-white/10'}`}>
      <Icon className="h-5 w-5" />{label}
    </button>
  );

  return (
    <FeaturesContext.Provider value={feat}>
    <div className="flex flex-col rounded-xl overflow-hidden border border-slate-700 min-h-[82vh] bg-slate-900 text-slate-100" data-testid="olfin-finance-module" onClick={() => menu && setMenu('')}>
      {/* TOP BAR */}
      <header className="flex items-center gap-3 px-4 h-14 bg-emerald-900/40 border-b border-slate-700" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <button className="flex items-center gap-2 font-semibold" onClick={() => setMenu(menu === 'company' ? '' : 'company')} data-testid="olfin-company-picker">
            <Landmark className="h-5 w-5 text-emerald-400" />{selected?.name || 'Select company'}<ChevronDown className="h-4 w-4" />
          </button>
          {menu === 'company' && (
            <div className={`absolute z-40 mt-1 w-64 rounded-lg border shadow-xl ${panel}`} data-testid="company-menu">
              {companies.map((c) => (
                <button key={c.company_id} onClick={() => { setSelectedId(c.company_id); go('home'); }} data-testid={`company-opt-${c.company_id}`}
                  className={`block w-full text-left px-3 py-2 text-sm hover:bg-emerald-600/10 ${c.company_id === selectedId ? 'text-emerald-500 font-medium' : ''}`}>{c.name}</button>
              ))}
              <button onClick={() => { setShowCreateCo(true); setMenu(''); }} className="block w-full text-left px-3 py-2 text-sm border-t border-slate-700 text-emerald-500" data-testid="company-add"><Plus className="h-4 w-4 inline mr-1" />Add company</button>
            </div>
          )}
        </div>
        <div className="flex-1 max-w-xl relative">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <input className="w-full h-9 pl-9 pr-3 rounded-full bg-slate-800 border border-slate-700 text-sm outline-none focus:border-emerald-500"
            placeholder="Search. Find transactions, contacts, reports, and more." value={sq}
            onChange={(e) => setSq(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && openAdvancedSearch()} data-testid="olfin-global-search" />
          {sres && (
            <div className={`absolute z-40 mt-1 w-full rounded-lg border shadow-xl max-h-96 overflow-auto ${panel}`} data-testid="search-results">
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-700 text-xs text-slate-400">
                <span>Search | <b className="text-slate-200">{sres.query}</b> · Press Enter for more results</span>
                <button onClick={() => setSres(null)}><X className="h-4 w-4" /></button>
              </div>
              {(() => {
                const groups = [
                  ['Transactions', (sres.transactions || []).map((t) => ({ key: t.txn_id,
                    render: `${t.type} | ${t.num || '—'} | ${t.name || '—'} | ${t.date || ''} | ${(((t.amount || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 }))}`,
                    status: t.status, onClick: () => go('form', 'txndetail', { txnId: t.txn_id }) }))],
                  ['Contacts', (sres.contacts || []).map((c) => ({ key: c.name_id, render: c.name, onClick: () => go(c.type === 'vendor' ? 'vendors_page' : 'customers_leads') }))],
                  ['Accounts', (sres.accounts || []).map((a) => ({ key: a.account_id, render: a.name, onClick: () => go('coa') }))],
                  ['Reports', (sres.reports || []).map((r) => ({ key: r, render: r, onClick: () => go('reports') }))],
                  ['Pages', (sres.pages || []).map((p) => ({ key: p.view, render: p.label, onClick: () => go(p.view) }))],
                ].filter(([, items]) => items.length);
                if (!groups.length) return <p className="px-3 py-3 text-sm text-slate-400">No matches.</p>;
                return groups.map(([label, items]) => (
                  <div key={label} className="py-1">
                    <p className="px-3 py-1 text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
                    {items.map((it) => (
                      <button key={it.key} onClick={it.onClick} data-testid={`search-item-${it.key}`}
                        className="flex items-center justify-between gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-emerald-600/10">
                        <span className="truncate tabular-nums">{it.render}</span>
                        {it.status && <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded-full ${it.status === 'Overdue' ? 'bg-rose-500/20 text-rose-300' : 'bg-slate-600/40 text-slate-300'}`}>{it.status}</span>}
                      </button>
                    ))}
                  </div>
                ));
              })()}
              <button onClick={openAdvancedSearch} data-testid="search-advanced"
                className="block w-full text-left px-3 py-2 text-sm text-emerald-400 border-t border-slate-700 hover:bg-emerald-600/10">Advanced transactions search for more results</button>
            </div>
          )}
        </div>
        <div className="relative">
          <button onClick={() => setMenu(menu === 'settings' ? '' : 'settings')} data-testid="olfin-settings" className="p-2 rounded-full hover:bg-white/10"><Settings className="h-5 w-5" /></button>
          {menu === 'settings' && (
            <div className={`absolute right-0 z-40 mt-1 rounded-xl border shadow-2xl p-4 grid grid-cols-4 gap-6 w-[760px] ${panel}`} data-testid="gear-menu">
              {menuOf(GEAR).map(([col, items]) => (
                <div key={col}>
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">{col}</p>
                  {items.map(([l, v, t]) => (
                    <button key={l} onClick={() => go(v, t)} data-testid={`gear-${tid(l)}`}
                      className="block w-full text-left py-1 text-sm hover:text-emerald-500">{l}</button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
        <a href="/help/finance" target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-white/10" data-testid="olfin-help"><HelpCircle className="h-5 w-5" /></a>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* LEFT RAIL */}
        <nav className="w-24 shrink-0 bg-slate-950/40 border-r border-slate-700 p-2 flex flex-col gap-1 relative" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setMenu(menu === 'create' ? '' : 'create')} data-testid="rail-create"
            className="w-full flex flex-col items-center gap-1 py-2.5 rounded-lg text-[11px] bg-emerald-600 text-white hover:bg-emerald-500"><Plus className="h-5 w-5" />Create</button>
          <div className="relative">{railBtn(Bookmark, 'Bookmarks', () => setMenu(menu === 'bookmarks' ? '' : 'bookmarks'), menu === 'bookmarks')}</div>
          {railBtn(HomeIcon, 'Home', () => go('home'), view === 'home')}
          {railBtn(BarChart3, 'Reports', () => go('reports'), view === 'reports')}
          {railBtn(Grid3x3, 'All apps', () => setAppsOpen((o) => !o), appsOpen)}
          <div className="border-t border-slate-700 my-1" />
          {pins.filter((k) => menuOf(ALLAPPS).some(([s]) => s === PIN_SECTION[k])).map((k) => { const [label, Icon] = PINNABLE[k]; const sec = PIN_SECTION[k]; const secDef = menuOf(ALLAPPS).find(([s]) => s === sec);
            return (
              <div key={k} className="relative">
                {railBtn(Icon, label, () => setMenu(menu === `pin-${k}` ? '' : `pin-${k}`), menu === `pin-${k}`)}
                {menu === `pin-${k}` && secDef && (
                  <div className={`absolute left-full top-0 ml-1 z-40 rounded-xl border shadow-2xl p-3 w-64 ${panel}`} data-testid={`pin-flyout-${k}`}>
                    <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">{sec}</p>
                    {secDef[1].map(([l, v, t]) => (
                      <button key={l} onClick={() => go(v, t)} data-testid={`pinitem-${k}-${tid(l)}`}
                        className="block w-full text-left py-1 text-sm hover:text-emerald-500">{l}</button>
                    ))}
                  </div>
                )}
              </div>
            ); })}
          <div className="mt-auto relative">{railBtn(Sliders, 'Customize', () => setMenu(menu === 'customize' ? '' : 'customize'), menu === 'customize')}</div>

          {/* CREATE MENU (4-column, Team omitted) */}
          {menu === 'create' && (
            <div className={`absolute left-24 top-2 z-40 rounded-xl border shadow-2xl p-4 grid grid-cols-4 gap-6 w-[720px] ${panel}`} data-testid="create-menu">
              {menuOf(CREATE).map(([col, items]) => (
                <div key={col}><p className="text-xs uppercase tracking-wide text-slate-400 mb-1.5">{col}</p>
                  {items.map(([label, v, t]) => (
                    <button key={label} onClick={() => go(v, t)} data-testid={`create-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                      className="block w-full text-left py-1 text-sm hover:text-emerald-500">{label}</button>
                  ))}
                </div>
              ))}
            </div>
          )}
          {menu === 'bookmarks' && (
            <div className={`absolute left-24 top-12 z-40 rounded-xl border shadow-2xl p-3 w-64 ${panel}`} data-testid="bookmarks-panel">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs uppercase text-slate-400">Bookmarks</span>
                {bookmarks.length > 0 && <button className="text-slate-400 hover:text-emerald-400" onClick={() => setBmEdit((e) => !e)} data-testid="bookmark-edit-toggle"><Pencil className="h-3.5 w-3.5" /></button>}
              </div>
              <button className="flex items-center gap-1.5 w-full text-left text-sm text-emerald-500 mb-2 hover:underline" onClick={bookmarkCurrent} data-testid="bookmark-add"><Plus className="h-3.5 w-3.5" />Bookmark current page</button>
              {bookmarks.length === 0 ? <p className="text-xs text-slate-400">No bookmarks yet.</p> :
                bookmarks.map((b, i) => (
                  <div key={i} className="flex items-center gap-1 py-0.5" data-testid={`bookmark-row-${i}`}>
                    {bmEdit ? (
                      <>
                        <input value={b.label} onChange={(e) => renameBookmark(i, e.target.value)} data-testid={`bookmark-rename-${i}`}
                          className="flex-1 h-7 rounded border border-slate-600 bg-slate-900 px-1.5 text-xs text-slate-100" />
                        <button className="text-rose-400" onClick={() => removeBookmark(i)} data-testid={`bookmark-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    ) : (
                      <button className="block w-full text-left py-1 text-sm hover:text-emerald-500 capitalize truncate" onClick={() => go(b.view, b.tab)} data-testid={`bookmark-open-${i}`}>{b.label}</button>
                    )}
                  </div>
                ))}
            </div>
          )}
          {menu === 'customize' && (
            <div className={`absolute left-24 bottom-2 z-40 rounded-xl border shadow-2xl p-3 w-52 ${panel}`} data-testid="customize-panel">
              <p className="text-xs uppercase text-slate-400 mb-1">Pin apps</p>
              {Object.entries(PINNABLE).filter(([k]) => menuOf(ALLAPPS).some(([s]) => s === PIN_SECTION[k])).map(([k, [label]]) => (
                <label key={k} className="flex items-center gap-2 py-1 text-sm" data-testid={`customize-${k}`}>
                  <input type="checkbox" checked={pins.includes(k)} onChange={() => togglePin(k)} />{label}
                </label>
              ))}
            </div>
          )}
        </nav>

        {/* PERSISTENT ALL-APPS SIDEBAR (expandable, active group open) */}
        {appsOpen && (() => {
          const activeSec = findSection(view, panelTab);
          return (
            <aside className="w-56 shrink-0 bg-slate-950/30 border-r border-slate-700 overflow-auto py-3" data-testid="allapps-sidebar" onClick={(e) => e.stopPropagation()}>
              {menuOf(ALLAPPS).map(([sec, items]) => {
                const isActiveGroup = activeSec && activeSec[0] === sec;
                const open = expandedGroup === sec || (expandedGroup === null && isActiveGroup);
                return (
                  <div key={sec} className="mb-1">
                    <button onClick={() => setExpandedGroup(open ? '' : sec)} data-testid={`sidebar-group-${tid(sec)}`}
                      className={`w-full flex items-center justify-between px-3 py-2 text-xs uppercase tracking-wide ${isActiveGroup ? 'text-emerald-400' : 'text-slate-400'} hover:text-slate-200`}>
                      {sec}<ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? '' : '-rotate-90'}`} />
                    </button>
                    {open && items.map(([label, v, t]) => {
                      const cur = v === view && (t || null) === (panelTab || null);
                      return (
                        <button key={label} onClick={() => go(v, t)} data-testid={`sidebar-item-${tid(label)}`}
                          className={`block w-full text-left pl-5 pr-3 py-1.5 text-sm border-l-2 ${cur ? 'border-emerald-500 bg-emerald-600/10 text-emerald-300 font-medium' : 'border-transparent text-slate-300 hover:bg-white/5'}`}>{label}</button>
                      );
                    })}
                  </div>
                );
              })}
            </aside>
          );
        })()}

        {/* CONTENT */}
        <main className="flex-1 min-w-0 overflow-auto p-5 bg-slate-100 dark:bg-slate-900 text-slate-800 dark:text-slate-100" data-testid="olfin-content">
          {error && <div className="mb-3 rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-400" data-testid="olfin-error">{error}</div>}
          {(() => { const sec = findSection(view, panelTab); const cur = sec && sec[1].find(([, v, t]) => v === view && (t || null) === (panelTab || null));
            return sec ? (
              <div className="text-sm text-slate-500 mb-2" data-testid="olfin-breadcrumb">{sec[0]}{cur ? ` / ${cur[0]}` : ''}</div>
            ) : null; })()}
          {(() => { const sec = findSection(view, panelTab); return sec ? (
            <div className="flex gap-1 border-b border-slate-300 dark:border-slate-700 mb-4 overflow-x-auto" data-testid="app-tabstrip">
              {sec[1].filter((it) => !it[3] || feat[it[3]]).map(([l, v, t]) => (
                <button key={l} onClick={() => go(v, t)} data-testid={`tab-${tid(l)}`}
                  className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${v === view && (t || null) === (panelTab || null) ? 'border-emerald-500 text-emerald-600 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'}`}>{l}</button>
              ))}
            </div>
          ) : null; })()}
          <div key={`${view}-${panelTab}-${navSeq}`}>{CONTENT()}</div>
        </main>
      </div>

      {/* CREATE COMPANY MODAL */}
      {showCreateCo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="olfin-create-form" onClick={() => setShowCreateCo(false)}>
          <div className={`w-full max-w-lg rounded-xl border p-5 ${panel}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3"><h3 className="font-semibold">New Finance Company</h3><button onClick={() => setShowCreateCo(false)}><X className="h-5 w-5" /></button></div>
            <form onSubmit={createCo} className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label>Company name</Label><Input value={coForm.name} required onChange={(e) => setCoForm({ ...coForm, name: e.target.value })} data-testid="olfin-input-name" /></div>
              <div className="space-y-1"><Label>Legal name</Label><Input value={coForm.legal_name} onChange={(e) => setCoForm({ ...coForm, legal_name: e.target.value })} data-testid="olfin-input-legal-name" /></div>
              <div className="space-y-1"><Label>Fiscal year start</Label>
                <Select value={coForm.fiscal_year_start_month} onValueChange={(v) => setCoForm({ ...coForm, fiscal_year_start_month: v })}>
                  <SelectTrigger data-testid="olfin-input-fy-month"><SelectValue /></SelectTrigger><SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>Company type / tax form</Label>
                <Select value={coForm.tax_form} onValueChange={(v) => setCoForm({ ...coForm, tax_form: v })}>
                  <SelectTrigger data-testid="olfin-input-tax-form"><SelectValue placeholder="Select a form" /></SelectTrigger><SelectContent>{taxForms.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1 sm:col-span-2"><Label>Starter chart of accounts</Label>
                <Select value={coForm.starter_chart} onValueChange={(v) => setCoForm({ ...coForm, starter_chart: v })}>
                  <SelectTrigger data-testid="olfin-input-starter-chart"><SelectValue /></SelectTrigger><SelectContent>{STARTER.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></div>
              <div className="sm:col-span-2 flex gap-2">
                <Button type="submit" disabled={saving || !coForm.name.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="olfin-save-company-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create company'}</Button>
                <Button type="button" variant="ghost" onClick={() => setShowCreateCo(false)} data-testid="olfin-cancel-company-btn">Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </FeaturesContext.Provider>
  );
}


// Bank transactions: the balance-card select and the review cards share one selected
// account so both default to the most-to-review account and stay in sync.
function BankTransactionsView({ base, onBack, go }) {
  const [acct, setAcct] = useState('');
  return (
    <>
      <BankBalanceCard base={base} acct={acct} onAcct={setAcct} />
      <BankReview base={base} onBack={onBack} acct={acct} onAcct={setAcct} onRegister={() => go('banking', 'register')} />
    </>
  );
}
